import React from 'react'

import { Input, Select } from '../../../main/components'
import { Button } from '../../../main/components/button'
import { DialogBody, DialogDismiss, DialogFooter } from '../../../main/components/dialog'
import { Heading, Label } from '../../../main/components/typography'
import { rangesToFilter } from '../utils'
import { FilterProps } from './types'

const ARRAY_DELIMITER = '|'
const OPTIONS = [
  { value: 'any', label: 'Any' },
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
]

const PRESETS_MAP: { [key: string]: string } = {
  'option|short': 'optionshort',
  'option|notshort': 'optionnotshort',
  'notoption|short': 'notoptionshort',
  'notoption|notshort': 'notoptionnotshort',
}

const DELIMITER = 'x'
const DEFAULT_RANGE = { from: '', to: '' }

export default function OptionsFilter({ onSubmit, config }: FilterProps) {
  const [optionable, setOptionable] = React.useState(OPTIONS[0].value)
  const [shortable, setShortable] = React.useState(OPTIONS[0].value)
  const [shortsalerestricted, setShortSaleRestricted] = React.useState(OPTIONS[0].value)
  const [halted, setHalted] = React.useState(OPTIONS[0].value)
  const [sharesRange, setSharesRange] = React.useState(DEFAULT_RANGE)
  const [usdRange, setUsdRange] = React.useState(DEFAULT_RANGE)

  const setValues = React.useCallback((item: string) => {
    const getYesNo = (value: string) => (value.includes('not') ? 'no' : 'yes')

    if (item.includes('to')) {
      const parts = item.split(DELIMITER)
      const [sharesRangeFrom = '', sharesRangeTo = ''] = parts[0].includes('to') ? parts[0].split('to') : []
      setSharesRange({ from: sharesRangeFrom, to: sharesRangeTo })

      if (parts.length === 2) {
        const [usdRangeFrom = '', usdRangeTo = ''] = parts[1].includes('to') ? parts[1].split('to') : []
        setUsdRange({
          from: usdRangeFrom && (Number(usdRangeFrom) / 1000).toFixed(2).replace('.00', ''),
          to: usdRangeTo && (Number(usdRangeTo) / 1000).toFixed(2).replace('.00', ''),
        })
      }

      return
    }

    switch (item) {
      case 'option':
      case 'notoption':
        setOptionable(getYesNo(item))
        break
      case 'short':
      case 'notshort':
        setShortable(getYesNo(item))
        break
      case 'shortsalerestricted':
      case 'notshortsalerestricted':
        setShortSaleRestricted(getYesNo(item))
        break
      case 'halted':
      case 'nothalted':
        setHalted(getYesNo(item))
        break
      case 'optionshort':
        setOptionable('yes')
        setShortable('yes')
        break
      case 'optionnotshort':
        setOptionable('yes')
        setShortable('no')
        break
      case 'notoptionshort':
        setOptionable('no')
        setShortable('yes')
        break
      case 'notoptionnotshort':
        setOptionable('no')
        setShortable('no')
        break
    }
  }, [])

  React.useEffect(() => {
    config.selectedValues.forEach((item) => {
      setValues(item)
    })
  }, [config, setValues])

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const str = [
      { option: optionable },
      { short: shortable },
      { shortsalerestricted: shortsalerestricted },
      { halted: halted },
    ]
      .filter((item) => Object.values(item).every((value) => value !== 'any'))
      .map((item) => Object.entries(item).map(([key, value]) => `${value === 'yes' ? '' : 'not'}${key}`))
      .join(ARRAY_DELIMITER)

    let rangesFilterDefs = [
      rangesToFilter([
        sharesRange,
        {
          from: usdRange.from && (Number(usdRange.from) * 1000).toFixed(2).replace('.00', ''),
          to: usdRange.to && (Number(usdRange.to) * 1000).toFixed(2).replace('.00', ''),
        },
      ]),
    ]

    if (rangesFilterDefs.length === 1 && rangesFilterDefs[0].length <= 1) {
      rangesFilterDefs = []
    }

    const filterDefs = [PRESETS_MAP[str] ?? str]
    onSubmit(filterDefs.concat(rangesFilterDefs))
  }

  return (
    <>
      <DialogBody className="relative flex w-145 max-w-full flex-col justify-center">
        <Heading level={5} className="mb-3 mt-0">
          Match all of the following:
        </Heading>
        <form id="js-smaFilerForm" onSubmit={handleSubmit}>
          <div className="flex">
            <div className="grid flex-1 grid-cols-2 gap-3">
              <Label title="Optionable">
                <Select
                  aria-label="Optionable"
                  items={OPTIONS}
                  value={optionable}
                  onChange={(item) => {
                    setOptionable(item.value)
                  }}
                />
              </Label>
              <Label title="Shortable">
                <Select
                  aria-label="Shortable"
                  items={OPTIONS}
                  value={shortable}
                  onChange={(item) => {
                    setShortable(item.value)
                  }}
                />
              </Label>
              <Label title="Short Sale Restricted">
                <Select
                  aria-label="Short Sale Restricted"
                  items={OPTIONS}
                  value={shortsalerestricted}
                  onChange={(item) => {
                    setShortSaleRestricted(item.value)
                  }}
                />
              </Label>
              <Label title="Halted">
                <Select
                  aria-label="Halted"
                  items={OPTIONS}
                  value={halted}
                  onChange={(item) => {
                    setHalted(item.value)
                  }}
                />
              </Label>
            </div>
          </div>

          <RangeInput
            label="Shares available to short (K)"
            from={sharesRange.from}
            to={sharesRange.to}
            onFromChange={(v) => setSharesRange({ ...sharesRange, from: v })}
            onToChange={(v) => setSharesRange({ ...sharesRange, to: v })}
          />

          <RangeInput
            label="USD available to short (M)"
            from={usdRange.from}
            to={usdRange.to}
            onFromChange={(v) => setUsdRange({ ...usdRange, from: v })}
            onToChange={(v) => setUsdRange({ ...usdRange, to: v })}
          />
        </form>
      </DialogBody>
      <DialogFooter>
        <Button as={DialogDismiss}>Cancel</Button>
        <Button type="submit" form="js-smaFilerForm" theme="blue">
          Submit
        </Button>
      </DialogFooter>
    </>
  )
}

interface IRangeInputProps {
  label: string
  from: string
  to: string
  onFromChange: (value: string) => void
  onToChange: (value: string) => void
}

function RangeInput(props: IRangeInputProps) {
  return (
    <div className="mt-3">
      <label className="mb-1 block text-2xs">{props.label}</label>
      <div className="flex flex-row items-center gap-1">
        <Input
          type="number"
          min={0}
          max={Number.MAX_SAFE_INTEGER}
          step={0.01}
          value={props.from}
          onChange={(e) => props.onFromChange(e.currentTarget.value)}
        />

        <div>{' - '}</div>

        <Input
          type="number"
          min={0}
          max={Number.MAX_SAFE_INTEGER}
          step={0.01}
          value={props.to}
          onChange={(e) => props.onToChange(e.currentTarget.value)}
        />
      </div>
    </div>
  )
}
